import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import { createStructuredSelector } from 'reselect';
import { connect, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import SearchIcon from '@material-ui/icons/Search';
import { debounce, find, get } from 'lodash';
import { useTranslation, Router } from '../../../../src/i18n';

import FbCircularProgress from '../../../../components/FbCircularProgress';
import Button from '../../../../components/Button';
import EditProductionCard from '../../../../components/EditProductionCard';
import {
  TP,
  performancesAggregationTypes,
  performancesAddedByIdentifiers,
  PRODUCTION_ADDED_IDENTIFIERS_IDS,
  PERFORMANCE_TYPES_ID,
  PRIVACY_ATTRIBUTE_IDS,
} from '../../../../constants/consts';
import FormikAutocompleteSelect from '../../../../components/Fields/FormikAutocompleteSelect';
import {
  createEntity,
  deleteEntity,
  getAggregationsAction,
  getList,
  updateOne,
  useBufferValueAction,
} from '../../../App/actions';
import { FILTER_AGGREGATION_SEARCH, PRODUCTIONS } from '../../../../constants/endpointConstants';
import { festivalPaths } from '../../../../constants/routes';
import FbPagination from '../../../../components/FbPagination';
import { defaultFestivalProductionsPagination, PRODUCTIONS_TYPES } from '../../constants';
import { selectFestival, selectPerformancesList } from '../../selectors';
import {
  selectActiveProfileId,
  selectAggregationsLoading,
  selectAggregationsOptions,
  selectBufferValueData,
} from '../../../App/selectors';
import { selectUserConstants } from '../../../../store/selectors';
import { getFestivalRoute } from '../../utils';
import { clearFestivalProductions, getFestivalProductionsAction } from '../../actions';
import usePermissions from '../../../../utils/permissions';
import { canDoProductionAction, checkDateForProductionDeletion } from '../../../../utils/common';
import ConfirmDeleteModal from '../../../../components/EditComponents/ConfirmDeleteModal';
import ActionStatusModal from '../../../../components/ActionStatusModal';
import H3 from '../../../../components/H3';
import TextButton from '../../../../components/TextButton';
import PublishProductionModal from '../../../../components/EditComponents/PublishProductionModal';

const useStyles = makeStyles(theme => ({
  title: {
    marginBottom: 24,
  },
  actionBlock: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 24,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  selectsBox: {
    flex: 1,
    width: '100%',
  },
  field: {
    maxWidth: 165,
    display: 'inline-block',
    marginRight: 10,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      maxWidth: '100%',
      marginBottom: theme.spacing(2),
    },
  },
  searchField: {
    maxWidth: 340,
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
    },
  },
  productionCard: {
    marginBottom: 20,
  },
}));

const { published, unpublished } = PRODUCTIONS_TYPES;
const { workAgg, typeAgg, queryAgg, festivalAgg, yearAgg } = performancesAggregationTypes;

const EditFestivalSeasons = ({
  festival,
  activeProfileId,
  userConstants,
  productions,
  musicalWorksList,
  aggregationLoadingName,
  publishingProductionData,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const urlQuery = useRouter().query;
  const { productionPermissions, isAdmin } = usePermissions(activeProfileId);

  const allSeasonsOption = { value: '', label: t(`${TP}.m_SEASON_ALL`) };

  const productionsTypeOptions = [
    {
      value: published,
      label: t(`${TP}.FN_EDITSEASON_PUBLISHED_STATUS`),
    },
    {
      value: unpublished,
      label: t(`${TP}.FN_EDITSEASON_UNPUBLISHED_STATUS`),
    },
  ];

  const [productionsType, setProductionsType] = React.useState(
    productionsTypeOptions.find(option => option?.value === urlQuery['productions-type']) || productionsTypeOptions[0],
  );
  const [mwValue, setMvValue] = React.useState(null);
  const [mwValueSearching, setMvValueSearching] = React.useState(false);
  const [seasonValue, setSeasonValue] = React.useState(allSeasonsOption);
  const [allSeasonsOptions, setSeasonsOptions] = React.useState([]);
  const [deletedProduction, setDeletedProduction] = React.useState(null);
  const [productionToCancel, setProductionToCancel] = React.useState(null);
  const [interactedProduction, setInteractedProduction] = React.useState(null);

  React.useEffect(
    () => {
      setSeasonsOptions([allSeasonsOption, ...festival?.years?.reverse().map(y => ({ value: y, label: y }))]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [festival],
  );

  React.useEffect(
    () => {
      if (!productions?.loadedOnce) {
        getProductions(productionsType?.value);
      }
      return () => dispatch(clearFestivalProductions());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const getProductions = (
    type = productionsType?.value,
    query = { [workAgg]: mwValue?.value },
    pagination = defaultFestivalProductionsPagination,
  ) => {
    let typeQuery = {};
    if (type === PRODUCTIONS_TYPES.unpublished) {
      typeQuery = {
        published: false,
        added_by: performancesAddedByIdentifiers.FESTIVAL,
        for_entity: PRODUCTION_ADDED_IDENTIFIERS_IDS.FESTIVAL,
        for_entity_id: festival?.id,
        sort: '-updated_at',
      };
    } else {
      typeQuery = {
        sort: '-year',
      };
    }
    dispatch(
      getList({
        endpoint: PRODUCTIONS,
        sagaRoutine: getFestivalProductionsAction,
        params: { queryParams: { festival_id: festival?.id, as_edit: true, ...typeQuery, ...query }, pagination },
      }),
    );
  };

  const getProductionsPage = pagination => getProductions(null, null, pagination);

  const seasonChange = value => {
    setSeasonValue(value);
    getProductions(null, { [workAgg]: mwValue?.value, [yearAgg]: value?.value });
  };

  const switchProductionsType = type => {
    const chosenType = type?.value;
    if (chosenType !== productionsType?.value) {
      Router.push(festivalPaths.edit, `${getFestivalRoute(festival)}/edit?tab=seasons&productions-type=${chosenType}`, {
        shallow: true,
      });
      setProductionsType(type);
      if (chosenType === PRODUCTIONS_TYPES.unpublished) {
        // published changed on unpublished => reset season option
        setSeasonValue(allSeasonsOption);
      }
      getProductions(chosenType);
    }
  };

  const getWorksList = debounce(value => {
    dispatch(
      getAggregationsAction({
        endpoint: FILTER_AGGREGATION_SEARCH(PRODUCTIONS),
        fieldName: workAgg,
        query: {
          ...(productionsType?.value !== PRODUCTIONS_TYPES.unpublished ? { [yearAgg]: seasonValue?.value } : {}), // allow in future add both option
          ...(productionsType?.value === PRODUCTIONS_TYPES.unpublished
            ? { added_by: performancesAddedByIdentifiers.COMPANY }
            : {}),
          [festivalAgg]: festival?.id,
          [typeAgg]: workAgg,
          [queryAgg]: value || '',
          published: productionsType?.value === PRODUCTIONS_TYPES.published,
        },
      }),
    );
  }, 500);

  const setMusicalWorkValue = option => {
    setMvValue(option);
    getProductions(productionsType?.value, { [workAgg]: option?.value || '' });
  };

  React.useEffect(() => {
    setMvValueSearching(false);
  }, [musicalWorksList]);

  const checkProductionAction = (permission, production) =>
    canDoProductionAction(permission, PRODUCTION_ADDED_IDENTIFIERS_IDS.FESTIVAL, festival?.id, production);

  const updateProduction = (productionId, options) => {
    dispatch(
      updateOne({
        endpoint: PRODUCTIONS,
        id: productionId,
        params: options,
        sagaRoutine: useBufferValueAction,
        callback: () => getProductions(defaultFestivalProductionsPagination),
      }),
    );
  };

  const publishProduction = () => {
    if (interactedProduction) {
      dispatch(
        createEntity({
          endpoint: `${PRODUCTIONS}/${interactedProduction.id}/actions`,
          sagaRoutine: useBufferValueAction,
          params: {
            type: 'publish',
            addedBy: { id: PRODUCTION_ADDED_IDENTIFIERS_IDS.FESTIVAL },
            approvedBy: { id: PRODUCTION_ADDED_IDENTIFIERS_IDS.FESTIVAL },
            privacyAttribute: {
              id: PRIVACY_ATTRIBUTE_IDS.PUBLIC,
            },
            festival: {
              id: festival?.id,
            },
          },
          callback: () => {
            getProductions(PRODUCTIONS_TYPES.unpublished);
            setInteractedProduction(null);
          },
        }),
      );
    }
  };

  const canDeleteProduction = production => {
    const isPublished = production?.published_at;
    let canDelete;
    if (isPublished) {
      canDelete =
        checkProductionAction(productionPermissions?.canDelete, production) &&
        checkDateForProductionDeletion(
          get(userConstants, '[0].MINIMUM_PRODUCTION_DELETION_DATE'),
          production?.published_at,
        );
    } else {
      canDelete = checkProductionAction(productionPermissions?.canDelete, production);
    }
    return isAdmin || productionPermissions?.canDeleteAsAdmin || canDelete
      ? () => setDeletedProduction({ data: production, type: isPublished ? 'published' : 'draft' })
      : null;
  };

  const deleteProduction = () => {
    if (deletedProduction) {
      if (deletedProduction?.type === 'draft') {
        dispatch(
          deleteEntity({
            endpoint: PRODUCTIONS,
            id: deletedProduction?.data?.id,
            callback: () => getProductions(defaultFestivalProductionsPagination),
          }),
        );
      } else if (deletedProduction?.type === 'published') {
        updateProduction(deletedProduction?.data?.id, {
          festivals: deletedProduction?.data?.festivals?.filter(c => c?.id !== festival?.id),
        });
      }
    }
    setDeletedProduction(null);
  };

  const cancelProduction = () => {
    updateProduction(productionToCancel?.id, {
      performanceTypes: [...productionToCancel?.performanceTypes, { id: PERFORMANCE_TYPES_ID.CANCELLED }],
    });
    setProductionToCancel(null);
  };

  return (
    <>
      <div className={classes.root}>
        <main>
          <div className={classes.actionBlock}>
            <div className={classes.selectsBox}>
              <FormikAutocompleteSelect
                instanceId="productions_type"
                field={{
                  name: 'productions_type',
                  value: productionsType,
                }}
                onChange={switchProductionsType}
                options={productionsTypeOptions}
                preventDefaultOnChange
                className={classes.field}
                label={t(`${TP}.M_INTRO_VIEW`)}
              />
              <FormikAutocompleteSelect
                instanceId="festival_year"
                field={{
                  name: 'festival_year',
                  value: seasonValue,
                }}
                onChange={seasonChange}
                options={productionsType?.value === published ? allSeasonsOptions : [allSeasonsOption]}
                preventDefaultOnChange
                className={classes.field}
                label={t(`${TP}.FN_SELECT_YEAR`)}
              />
              <FormikAutocompleteSelect
                className={cn(classes.field, classes.searchField)}
                instanceId="musical_work_search"
                label={t(`${TP}.m_MUSICALWORK`)}
                field={{
                  name: 'musical_work',
                  value: mwValue,
                }}
                placeholder={t(`${TP}.FN_SEARCH_BY_MUSICAL_WORK`)}
                onChange={setMusicalWorkValue}
                onInputTextChange={v => {
                  getWorksList(v);
                  setMvValueSearching(true);
                }}
                options={musicalWorksList || []}
                isLoading={aggregationLoadingName === workAgg}
                isClearable
                isEditable
                customComponents={{
                  DropdownIndicator: () => <SearchIcon />,
                }}
                onFocus={() => {
                  getWorksList('');
                  setMvValueSearching(true);
                }}
                startAggregations={mwValueSearching}
              />
            </div>
            <Button
              onClick={() =>
                Router.push({
                  pathname: `${getFestivalRoute(festival)}/edit/modify-production`,
                  query: { step: 1 },
                })
              }
            >
              {t(`${TP}.m_ADDSCHEDULE`)}
            </Button>
          </div>
          <>
            {productions?.loading ? (
              <FbCircularProgress />
            ) : (
              <>
                {(productions?.data || []).map(p => (
                  <EditProductionCard
                    production={p}
                    key={p?.id}
                    onCancel={
                      (isAdmin || checkProductionAction(productionPermissions?.canCancel, p)) &&
                      !find(p?.performanceTypes, { id: PERFORMANCE_TYPES_ID.CANCELLED })
                        ? () => setProductionToCancel(p)
                        : null
                    }
                    className={classes.productionCard}
                    editProductionLinkProps={step => ({
                      pathname: `${getFestivalRoute(festival)}/edit/modify-production`,
                      query: { production_id: p?.id, step },
                    })}
                    onDelete={canDeleteProduction(p)}
                    onPublish={
                      (isAdmin || productionPermissions.canPublishAll) && !p?.published_at
                        ? () => setInteractedProduction(p)
                        : null
                    }
                  />
                ))}
                <FbPagination filters={productions} fetchFunction={getProductionsPage} />
                {!productions?.loading &&
                  productions?.loadedOnce &&
                  productions?.data?.length === 0 &&
                  t(`${TP}.NO_RESULTS`)}
              </>
            )}
          </>
        </main>
      </div>
      {interactedProduction?.id && (
        <PublishProductionModal
          production={interactedProduction}
          closeModal={() => setInteractedProduction(null)}
          publish={publishProduction}
          isPublishing={publishingProductionData?.loading}
        />
      )}
      {deletedProduction?.data?.id && (
        <ConfirmDeleteModal
          closeModal={() => setDeletedProduction(null)}
          onDelete={deleteProduction}
          title={t(`${TP}.FN_EDITSEASON_APPROVE_DELETE_PRODUCTION_TITLE`, { name: deletedProduction?.name })}
          text={t(`${TP}.FN_EDITSEASON_APPROVE_DELETE_PRODUCTION_INFO`)}
        />
      )}
      {productionToCancel && (
        <ActionStatusModal
          isOpened
          onClose={() => setProductionToCancel(null)}
          type="warning"
          title={
            <H3>{t(`${TP}.FN_EDITSEASON_APPROVE_CANCELE_PRODUCTION_TITLE`, { name: productionToCancel?.name })}</H3>
          }
          buttons={[
            <TextButton onClick={() => setProductionToCancel(null)} key="approve-button">
              {t(`${TP}.m_CANCEL`)}
            </TextButton>,
            <Button onClick={cancelProduction} key="cancel-button">
              {t(`${TP}.FN_EDITSEASON_MARK_AS_CANCELLED`)}
            </Button>,
          ]}
        >
          {t(`${TP}.FN_EDITSEASON_APPROVE_CANCELE_PRODUCTION_INFO`)}
        </ActionStatusModal>
      )}
    </>
  );
};

EditFestivalSeasons.propTypes = {
  festival: PropTypes.object.isRequired,
  activeProfileId: PropTypes.number,
  userConstants: PropTypes.array,
  productions: PropTypes.object,
};

const mapStateToProps = createStructuredSelector({
  festival: selectFestival(),
  activeProfileId: selectActiveProfileId(),
  userConstants: selectUserConstants(),
  productions: selectPerformancesList(),
  musicalWorksList: selectAggregationsOptions(workAgg),
  aggregationLoadingName: selectAggregationsLoading(),
  publishingProductionData: selectBufferValueData(),
});

const withConnect = connect(mapStateToProps, null);

export default withConnect(EditFestivalSeasons);
